
import { useCallback, useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useSupabase } from "@/hooks/useSupabase";
import { supabase } from "@/integrations/supabase/client";
import { formatArticleId } from '@/utils/articleIdValidation';

export const useArticleLike = (articleId: string) => {
  const { user } = useSupabase();
  const { toast } = useToast();
  const [isLiked, setIsLiked] = useState(false);
  const [isCheckingLike, setIsCheckingLike] = useState(true);

  // Format article ID for database operations
  const dbArticleId = formatArticleId(articleId);

  // Check if article is liked on mount
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!user) {
        setIsCheckingLike(false);
        return;
      }

      try {
        const { data: like } = await supabase
          .from('article_likes')
          .select('id')
          .eq('article_id', dbArticleId)
          .eq('user_id', user.id)
          .maybeSingle();

        setIsLiked(!!like);
      } catch (error) {
        console.error("Error checking like status:", error);
      } finally {
        setIsCheckingLike(false);
      }
    };

    checkLikeStatus();
  }, [articleId, dbArticleId, user]);

  const handleLike = useCallback(async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to like articles",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log("Handling like for article:", articleId);
      console.log("Database article ID:", dbArticleId);
      
      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from('article_likes')
          .delete()
          .eq('article_id', dbArticleId)
          .eq('user_id', user.id);

        if (error) throw error;
        setIsLiked(false);
        toast({
          title: "Article unliked",
          description: "Article has been removed from your likes"
        });
      } else {
        // Like
        const { error } = await supabase
          .from('article_likes')
          .insert({ 
            article_id: dbArticleId,
            user_id: user.id 
          });

        if (error) {
          console.error("Like insertion error:", error);
          throw error;
        }
        setIsLiked(true);
        toast({
          title: "Article liked",
          description: "Article has been added to your likes"
        });
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast({
        title: "Error",
        description: "Failed to update article like status",
        variant: "destructive"
      });
    }
  }, [articleId, dbArticleId, user, toast, isLiked]);

  return {
    handleLike,
    isLiked,
    isCheckingLike
  };
};
